import { createSelector } from '@reduxjs/toolkit'
import {
  getCanvasConfig,
  getContextMenuConfig,
  getPropertiesPanelConfig,
  getPaletteConfig
} from '../../views/apps/engagement/marketing-automation/journey-builder/core/ui-configs'
import { isEqual } from 'lodash'

export const selectState = state => state
export const selectUI = state => state.ui
export const selectWorkflow = state => state.workflow
export const selectDiagramNodeChanges = state => state.changes
export const selectMode = state => state.mode
const EMPTY_MAP = {}
export const selectJourneyPaused = state => state.mode === 'paused'
export const selectPalette = state => state.ui.palette
export const selectPaletteNodeMap = state => state.ui.palette.nodeMap ?? EMPTY_MAP
export const selectPaletteNodesLoaded = state => Object.keys(state.ui.palette.nodeMap ?? EMPTY_MAP).length > 0
export const selectInfoPanel = state => state.ui.infoPanel
export const selectMockMode = state => state.ui.mockMode
export const selectErrorStack = state => state.ui.errorStack
export const selectHasStartNode = createSelector(
  [selectWorkflow],
  workflow =>
    Object.values(workflow?.nodes || {}).some(
      node => node?.type === 'start' || node?.type === 'START'
    )
)

/**
 * Select the zoom scale.
 * @param {JBState} state - The current state.
 * @returns {number|string} The zoom scale.
 */
export const selectZoom = state => state.ui.zoomScale
/**
 * Select the history state.
 * @param {JBState} state - The current state.
 * @returns {HistoryState} The history state.
 */
export const selectHistory = state => state.ui.history
/**
 * Select the selection state.
 * @param {JBState} state - The current state.
 * @returns {SelectionState} The selection state.
 */
export const selectSelection = state => state.ui.selection

export const selectPopupData = state => state.ui.popupData

/**
 * Select a node from the workflow by nodeId.
 * @param {JBState} state - The current state.
 * @param {string} nodeId - The node ID to select.
 * @returns {Object|undefined} The node object or undefined if not found.
 */
export const selectNodeById = (state, nodeId) => state.workflow.nodes?.[nodeId]

/**
 * Select the clipboard state.
 * @param {JBState} state - The current state.
 * @returns {ClipboardState} The clipboard state.
 */
export const selectClipboard = state => state.ui.clipboard
/**
 * Select the palette config.
 * @returns {PaletteConfig} The palette state.
 */
export const selectPaletteConfig = createSelector(
  [selectPalette, selectMode],
  (palette, mode) => getPaletteConfig(palette, mode, palette.nodes || []),
  {
    memoizeOptions: {
      equalityCheck: (a, b) => isEqual(a, b)
    }
  }
)
/**
 * Select the properties panel config.
 * @returns {PropertiesPanelConfig} The properties panel state.
 */
export const selectPropertiesPanel = createSelector(
  [selectUI, selectWorkflow, selectMode, selectMockMode],
  ({ propertiesPanel, selection, mockMode }, workflow, mode) =>
    getPropertiesPanelConfig(propertiesPanel, selection, workflow.nodes, mode, mockMode),
  {
    memoizeOptions: {
      equalityCheck: (a, b) => isEqual(a, b)
    }
  }
)
/**
 * Select the context menu configuration.
 * @returns {ContextMenuConfig} The context menu configuration.
 */
export const selectContextMenu = createSelector(
  [selectUI],
  ({ contextMenu, selection, clipboard }) => getContextMenuConfig(contextMenu, selection, !!clipboard),
  {
    memoizeOptions: {
      equalityCheck: (a, b) => isEqual(a, b)
    }
  }
)
/**
 * Select the canvas component configuration.
 * @returns {CanvasConfig} The canvas configuration.
 */
export const selectCanvasConfig = createSelector(
  [selectZoom, selectSelection, selectDiagramNodeChanges, selectMode, selectMockMode],
  getCanvasConfig,
  {
    memoizeOptions: {
      equalityCheck: (a, b) => isEqual(a, b)
    }
  }
)

/** @typedef {{ workflowName: string, history: HistoryState, mode: WorkflowMode, translateName: boolean }} ToolbarConfig */
/**
 * Select the toolbar configuration.
 * @returns {ToolbarConfig} The canvas configuration.
 */
export const selectToolbarConfig = createSelector(
  [selectHistory, selectWorkflow, selectMode, selectPalette],
  (history, workflow, mode, palette) => ({
    history,
    workflowName: workflow.name,
    mode,
    translateName: mode === 'edit' && !palette.expanded
  }),
  {
    memoizeOptions: {
      equalityCheck: (a, b) => isEqual(a, b)
    }
  }
)
